/**
 * Risk Assessment — Identifies production risks per scene.
 * Part of the Greenlight Engine for 1895.ai.
 *
 * Enhanced with a D3-powered scatter plot above the risk card list.
 */
import { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Shield, AlertTriangle, ChevronDown, ChevronUp, BarChart3, List } from 'lucide-react';
import * as d3 from 'd3';

const RISK_TYPE_LABELS = {
  weather_dependency: 'Weather',
  technical_complexity: 'Technical',
  vfx_uncertainty: 'VFX',
  schedule_fragility: 'Schedule',
  location_risk: 'Location',
  talent_bottleneck: 'Talent',
  safety_concerns: 'Safety',
  regulatory_legal: 'Legal/Regulatory',
  child_animal: 'Children/Animals',
  reshoot_probability: 'Reshoot Risk',
};

const RISK_LEVEL_STYLES = {
  critical: {
    border: 'border-red-500/30',
    bg: 'bg-red-500/5',
    badge: 'bg-red-500/15 text-red-400',
    diamond: 'border-red-500 bg-black',
    barColor: '#DC143C',
  },
  high: {
    border: 'border-red-500/30',
    bg: 'bg-red-500/5',
    badge: 'bg-red-500/15 text-red-400',
    diamond: 'border-transparent bg-red-500',
    barColor: '#FF6B6B',
  },
  medium: {
    border: 'border-yellow-500/30',
    bg: 'bg-yellow-500/5',
    badge: 'bg-yellow-500/15 text-yellow-400',
    diamond: 'border-transparent bg-yellow-500',
    barColor: '#FFD700',
  },
  low: {
    border: 'border-emerald-500/30',
    bg: 'bg-emerald-500/5',
    badge: 'bg-emerald-500/15 text-emerald-400',
    diamond: 'border-transparent bg-emerald-500',
    barColor: '#4CAF50',
  },
};

const SCATTER_DIAMOND_COLORS = {
  critical: '#EF4444',
  high: '#FF6B35',
  moderate: '#F59E0B',
  medium: '#F59E0B',
  low: '#22C55E',
};

const SCATTER_DIAMOND_SIZES = {
  critical: 16,
  high: 12,
  moderate: 8,
  medium: 8,
  low: 6,
};

function scoreColor(score) {
  if (score <= 30) return 'text-emerald-400';
  if (score <= 50) return 'text-amber-400';
  if (score <= 70) return 'text-orange-400';
  return 'text-red-400';
}

function severityBarColor(severity) {
  if (severity >= 80) return '#DC143C';
  if (severity >= 60) return '#FF8C42';
  if (severity >= 40) return '#FFD700';
  return '#4CAF50';
}

function RiskDiamond({ level }) {
  const style = RISK_LEVEL_STYLES[level] || RISK_LEVEL_STYLES.low;
  return (
    <span
      className={`inline-block w-3 h-3 rotate-45 border-2 shrink-0 ${style.diamond}`}
    />
  );
}

/* ─── Scatter Plot ─── */
function RiskScatterPlot({ scenes, onDiamondClick }) {
  const containerRef = useRef(null);
  const [dims, setDims] = useState({ width: 400, height: 200 });
  const [tooltip, setTooltip] = useState(null);
  const [entered, setEntered] = useState(false);

  // Responsive width
  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setDims({ width: entry.contentRect.width, height: 200 });
      }
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  // Entrance animation trigger
  useEffect(() => {
    const timer = setTimeout(() => setEntered(true), 50);
    return () => clearTimeout(timer);
  }, []);

  const margin = { top: 16, right: 20, bottom: 28, left: 40 };
  const innerW = dims.width - margin.left - margin.right;
  const innerH = dims.height - margin.top - margin.bottom;

  const sceneNumbers = scenes.map((s) => s.scene_number);
  const maxScene = Math.max(...sceneNumbers, 1);

  const xScale = useMemo(
    () => d3.scaleLinear().domain([0, maxScene + 1]).range([0, innerW]),
    [maxScene, innerW],
  );
  const yScale = useMemo(
    () => d3.scaleLinear().domain([0, 100]).range([innerH, 0]),
    [innerH],
  );

  // X-axis ticks
  const xTicks = useMemo(() => {
    const step = Math.max(1, Math.ceil(maxScene / 10));
    const ticks = [];
    for (let i = step; i <= maxScene; i += step) ticks.push(i);
    return ticks;
  }, [maxScene]);

  // Y-axis ticks
  const yTicks = [0, 25, 50, 75, 100];

  // Act dividers at 25%, 50%, 75% of scene range
  const actDividers = [0.25, 0.5, 0.75].map((pct) => Math.round(maxScene * pct));

  return (
    <div ref={containerRef} className="relative w-full rounded-lg overflow-hidden" style={{ height: 200, backgroundColor: '#09090B' }}>
      <svg width={dims.width} height={dims.height}>
        <g transform={`translate(${margin.left},${margin.top})`}>
          {/* Grid lines */}
          {yTicks.map((t) => (
            <line
              key={`y-${t}`}
              x1={0}
              x2={innerW}
              y1={yScale(t)}
              y2={yScale(t)}
              stroke="#222228"
              strokeWidth={1}
            />
          ))}
          {xTicks.map((t) => (
            <line
              key={`x-${t}`}
              x1={xScale(t)}
              x2={xScale(t)}
              y1={0}
              y2={innerH}
              stroke="#222228"
              strokeWidth={1}
            />
          ))}

          {/* Act dividers */}
          {actDividers.map((scn, i) => (
            <g key={`act-${i}`}>
              <line
                x1={xScale(scn)}
                x2={xScale(scn)}
                y1={0}
                y2={innerH}
                stroke="#C9A84C"
                strokeWidth={1}
                strokeDasharray="4 3"
                opacity={0.5}
              />
              <text
                x={xScale(scn)}
                y={-4}
                textAnchor="middle"
                fill="#C9A84C"
                fontSize={9}
                opacity={0.6}
              >
                Act {i + 2}
              </text>
            </g>
          ))}

          {/* X-axis labels */}
          {xTicks.map((t) => (
            <text
              key={`xl-${t}`}
              x={xScale(t)}
              y={innerH + 16}
              textAnchor="middle"
              fill="#555"
              fontSize={9}
            >
              {t}
            </text>
          ))}
          <text
            x={innerW / 2}
            y={innerH + 26}
            textAnchor="middle"
            fill="#666"
            fontSize={9}
          >
            Scene
          </text>

          {/* Y-axis labels */}
          {yTicks.map((t) => (
            <text
              key={`yl-${t}`}
              x={-8}
              y={yScale(t) + 3}
              textAnchor="end"
              fill="#555"
              fontSize={9}
            >
              {t}
            </text>
          ))}

          {/* Diamond markers */}
          {scenes.map((scene, idx) => {
            const level = scene.risk_level || 'low';
            const color = SCATTER_DIAMOND_COLORS[level] || SCATTER_DIAMOND_COLORS.low;
            const size = SCATTER_DIAMOND_SIZES[level] || 6;
            const half = size / 2;
            const cx = xScale(scene.scene_number);
            const cy = yScale(scene.risk_score);

            return (
              <g
                key={scene.scene_number}
                style={{
                  cursor: 'pointer',
                  opacity: entered ? 1 : 0,
                  transform: entered ? 'scale(1)' : 'scale(0)',
                  transformOrigin: `${cx + margin.left}px ${cy + margin.top}px`,
                  transition: `opacity 0.4s ease ${idx * 30}ms, transform 0.4s ease ${idx * 30}ms`,
                }}
                onMouseEnter={(e) => {
                  const rect = containerRef.current?.getBoundingClientRect();
                  setTooltip({
                    scene,
                    x: cx + margin.left,
                    y: cy + margin.top,
                  });
                }}
                onMouseLeave={() => setTooltip(null)}
                onClick={() => onDiamondClick?.(scene)}
              >
                <rect
                  x={cx - half}
                  y={cy - half}
                  width={size}
                  height={size}
                  fill={color}
                  transform={`rotate(45, ${cx}, ${cy})`}
                  stroke="rgba(0,0,0,0.5)"
                  strokeWidth={0.5}
                />
              </g>
            );
          })}
        </g>
      </svg>

      {/* Tooltip */}
      {tooltip && (
        <div
          className="absolute pointer-events-none z-20 px-3 py-2 rounded-lg text-xs"
          style={{
            left: tooltip.x,
            top: tooltip.y - 10,
            transform: 'translate(-50%, -100%)',
            backgroundColor: '#1A1A1F',
            border: '1px solid #333',
            color: '#E5E5E5',
            maxWidth: 220,
          }}
        >
          <div className="font-medium" style={{ color: SCATTER_DIAMOND_COLORS[tooltip.scene.risk_level] || '#888' }}>
            Scene #{tooltip.scene.scene_number} — {tooltip.scene.risk_level}
          </div>
          <div className="text-[var(--text-secondary)] mt-0.5">{tooltip.scene.slug_line}</div>
          <div className="text-[var(--text-muted)] mt-0.5">
            Severity: {tooltip.scene.risk_score} · {RISK_TYPE_LABELS[tooltip.scene.risk_factors?.[0]?.type] || 'Multiple risks'}
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Scene Risk Card ─── */
function SceneRiskCard({ scene, isExpanded, onToggle, cardRef }) {
  const style = RISK_LEVEL_STYLES[scene.risk_level] || RISK_LEVEL_STYLES.medium;

  return (
    <div ref={cardRef} className={`rounded-lg border ${style.border} ${style.bg}`}>
      {/* Collapsed header */}
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-3 p-3 text-left"
      >
        <RiskDiamond level={scene.risk_level} />
        <span className="text-[11px] font-mono text-[var(--text-muted)] shrink-0">
          #{scene.scene_number}
        </span>
        <span className="flex-1 text-xs text-[var(--text-secondary)] truncate">
          {scene.slug_line}
        </span>
        <span className={`text-xs font-bold shrink-0 ${scoreColor(scene.risk_score)}`}>
          {scene.risk_score}
        </span>
        {isExpanded
          ? <ChevronUp size={14} className="text-[var(--text-muted)] shrink-0" />
          : <ChevronDown size={14} className="text-[var(--text-muted)] shrink-0" />
        }
      </button>

      {/* Expanded content */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-3 pb-3 space-y-3 border-t border-[var(--border-subtle)]">
              {/* Risk factors */}
              {scene.risk_factors?.length > 0 && (
                <div className="space-y-2 pt-3">
                  <h5 className="text-[11px] font-semibold text-[var(--text-secondary)]">
                    Risk Factors
                  </h5>
                  {scene.risk_factors.map((factor, i) => (
                    <div key={i} className="space-y-1">
                      <div className="flex items-center gap-2 text-xs">
                        <span className="w-28 text-[var(--text-muted)] truncate shrink-0">
                          {RISK_TYPE_LABELS[factor.type] || factor.type.replace(/_/g, ' ')}
                        </span>
                        <div className="flex-1 h-2.5 rounded-full bg-[var(--bg-app)] overflow-hidden">
                          <motion.div
                            initial={{ width: 0 }}
                            animate={{ width: `${factor.severity}%` }}
                            transition={{ duration: 0.4, ease: 'easeOut' }}
                            className="h-full rounded-full"
                            style={{ backgroundColor: severityBarColor(factor.severity) }}
                          />
                        </div>
                        <span className="w-7 text-right text-[var(--text-secondary)] font-medium text-[11px]">
                          {factor.severity}
                        </span>
                      </div>
                      <p className="text-[11px] text-[var(--text-secondary)] pl-[7.5rem]">
                        {factor.description}
                      </p>
                      {factor.mitigation && (
                        <p className="text-[10px] text-[var(--text-muted)] pl-[7.5rem] flex items-start gap-1">
                          <Shield size={10} className="text-emerald-400 mt-0.5 shrink-0" />
                          {factor.mitigation}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Contingency */}
              {scene.contingency && (
                <div className="flex items-start gap-2 rounded-md bg-[var(--bg-app)] p-2 text-xs">
                  <AlertTriangle size={12} className="text-amber-400 mt-0.5 shrink-0" />
                  <div>
                    <span className="text-[10px] font-semibold text-[var(--text-muted)] uppercase tracking-wide">
                      Contingency
                    </span>
                    <p className="text-[var(--text-secondary)] mt-0.5">{scene.contingency}</p>
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ─── Main Component ─── */
export default function RiskAssessment({ data }) {
  const [expandedIndex, setExpandedIndex] = useState(null);
  const [viewMode, setViewMode] = useState('both'); // 'both' | 'chart' | 'list'
  const cardRefs = useRef({});

  const scenes = data?.scenes || [];
  const summary = data?.summary;

  const filteredScenes = useMemo(
    () =>
      scenes
        .filter((s) => s.risk_level === 'critical' || s.risk_level === 'high' || s.risk_level === 'medium')
        .sort((a, b) => b.risk_score - a.risk_score),
    [scenes],
  );

  const handleToggle = (i) => {
    setExpandedIndex(expandedIndex === i ? null : i);
  };

  const handleDiamondClick = useCallback((scene) => {
    // Find index in filteredScenes
    const idx = filteredScenes.findIndex((s) => s.scene_number === scene.scene_number);
    if (idx >= 0) {
      setExpandedIndex(idx);
      // Ensure list is visible
      if (viewMode === 'chart') setViewMode('both');
      // Scroll to card
      setTimeout(() => {
        const el = cardRefs.current[scene.scene_number];
        el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    }
  }, [filteredScenes, viewMode]);

  const maxCategorySeverity = summary?.top_risk_categories?.length
    ? Math.max(...summary.top_risk_categories.map((c) => c.avg_severity), 1)
    : 1;

  if (!data) return null;

  const showChart = viewMode === 'both' || viewMode === 'chart';
  const showList = viewMode === 'both' || viewMode === 'list';

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-[var(--text-primary)] flex items-center gap-2">
          <Shield size={16} className="text-[var(--text-muted)]" />
          Risk Assessment
        </h3>
        <div className="flex items-center gap-3">
          {/* View toggle */}
          {scenes.length > 0 && (
            <div className="flex rounded-md border border-[var(--border-subtle)] overflow-hidden text-[10px]">
              <button
                onClick={() => setViewMode('chart')}
                className={`px-2 py-1 flex items-center gap-1 transition-colors ${
                  viewMode === 'chart'
                    ? 'bg-[var(--bg-elevated)] text-[var(--text-primary)]'
                    : 'text-[var(--text-muted)] hover:text-[var(--text-secondary)]'
                }`}
              >
                <BarChart3 size={10} /> Chart
              </button>
              <button
                onClick={() => setViewMode('both')}
                className={`px-2 py-1 transition-colors ${
                  viewMode === 'both'
                    ? 'bg-[var(--bg-elevated)] text-[var(--text-primary)]'
                    : 'text-[var(--text-muted)] hover:text-[var(--text-secondary)]'
                }`}
              >
                Both
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`px-2 py-1 flex items-center gap-1 transition-colors ${
                  viewMode === 'list'
                    ? 'bg-[var(--bg-elevated)] text-[var(--text-primary)]'
                    : 'text-[var(--text-muted)] hover:text-[var(--text-secondary)]'
                }`}
              >
                <List size={10} /> List
              </button>
            </div>
          )}
          {summary?.overall_risk_score != null && (
            <span className={`text-lg font-bold ${scoreColor(summary.overall_risk_score)}`}>
              {summary.overall_risk_score}/100
            </span>
          )}
        </div>
      </div>

      {/* Severity breakdown counts */}
      {summary && (
        <div className="flex gap-3">
          <div className="flex-1 p-3 rounded-lg bg-[var(--bg-app)] text-center">
            <div className="text-lg font-bold text-red-400">{summary.critical_count ?? 0}</div>
            <div className="text-[10px] text-[var(--text-muted)] uppercase tracking-wide">Critical</div>
          </div>
          <div className="flex-1 p-3 rounded-lg bg-[var(--bg-app)] text-center">
            <div className="text-lg font-bold text-orange-400">{summary.high_count ?? 0}</div>
            <div className="text-[10px] text-[var(--text-muted)] uppercase tracking-wide">High</div>
          </div>
          <div className="flex-1 p-3 rounded-lg bg-[var(--bg-app)] text-center">
            <div className="text-lg font-bold text-yellow-400">{summary.medium_count ?? 0}</div>
            <div className="text-[10px] text-[var(--text-muted)] uppercase tracking-wide">Medium</div>
          </div>
          <div className="flex-1 p-3 rounded-lg bg-[var(--bg-app)] text-center">
            <div className="text-lg font-bold text-emerald-400">{summary.low_count ?? 0}</div>
            <div className="text-[10px] text-[var(--text-muted)] uppercase tracking-wide">Low</div>
          </div>
        </div>
      )}

      {/* Scatter Plot */}
      <AnimatePresence>
        {showChart && scenes.length > 0 && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: 'easeOut' }}
            className="overflow-hidden"
          >
            <RiskScatterPlot
              scenes={scenes}
              onDiamondClick={handleDiamondClick}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Top risk categories */}
      {summary?.top_risk_categories?.length > 0 && (
        <div className="rounded-lg bg-[var(--bg-elevated)] border border-[var(--border-subtle)] p-4 space-y-2">
          <p className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] mb-2">
            Top Risk Categories
          </p>
          {summary.top_risk_categories.map((cat, i) => {
            const width = (cat.avg_severity / maxCategorySeverity) * 100;
            return (
              <div key={i} className="flex items-center gap-2 text-xs">
                <span className="w-28 text-[var(--text-muted)] truncate shrink-0">
                  {RISK_TYPE_LABELS[cat.category] || cat.category.replace(/_/g, ' ')}
                </span>
                <div className="flex-1 h-3 rounded-full bg-[var(--bg-app)] overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${width}%` }}
                    transition={{ duration: 0.4, ease: 'easeOut', delay: i * 0.05 }}
                    className="h-full rounded-full"
                    style={{ backgroundColor: severityBarColor(cat.avg_severity) }}
                  />
                </div>
                <span className="w-7 text-right text-[var(--text-secondary)] font-medium">
                  {cat.avg_severity}
                </span>
                <span className="text-[10px] text-[var(--text-muted)] w-16 text-right shrink-0">
                  {cat.scene_count} scene{cat.scene_count !== 1 ? 's' : ''}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* Scene risk list (medium+ only) */}
      {showList && filteredScenes.length > 0 && (
        <div className="space-y-2">
          <p className="text-[10px] uppercase tracking-wider text-[var(--text-muted)]">
            Scenes at Risk ({filteredScenes.length})
          </p>
          {filteredScenes.map((scene, i) => (
            <SceneRiskCard
              key={scene.scene_number}
              scene={scene}
              isExpanded={expandedIndex === i}
              onToggle={() => handleToggle(i)}
              cardRef={(el) => { cardRefs.current[scene.scene_number] = el; }}
            />
          ))}
        </div>
      )}

      {/* Risk assessment text */}
      {summary?.risk_assessment && (
        <p className="text-xs text-[var(--text-secondary)] p-3 rounded-lg bg-[var(--bg-elevated)] border border-[var(--border-subtle)]">
          {summary.risk_assessment}
        </p>
      )}
    </div>
  );
}
